import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { 
  CreditCard,
  Calendar,
  Loader2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  ExternalLink,
  FileText,
  ArrowRight,
  Building2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * BusinessBilling - /business/billing
 * 
 * Shows:
 * - Current subscription summary (plan, price, cadence)
 * - Next billing date
 * - Update payment method (Stripe portal)
 * - Cancel/Reactivate subscription
 * - Invoice history placeholder
 */
const BusinessBilling = () => {
  const { user, token, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  
  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    if (!user.is_business) {
      toast.error("Business account required");
      navigate('/');
      return;
    }
    fetchBilling();
  }, [user, navigate]);
  
  const fetchBilling = async () => {
    try {
      const res = await fetch(`${API_URL}/api/business/dashboard/billing`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setData(await res.json());
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to load billing info");
      }
    } catch (error) {
      toast.error("Failed to load billing info");
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel? You will retain access until the end of your current billing period.')) {
      return;
    }
    
    setActionLoading('cancel');
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Subscription will be canceled at period end");
        refreshUser();
        fetchBilling();
      } else {
        const error = await res.json();
        throw new Error(error.detail || "Failed to cancel");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };
  
  const handleReactivateSubscription = async () => {
    setActionLoading('reactivate');
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/reactivate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Subscription reactivated!");
        refreshUser();
        fetchBilling();
      } else {
        const error = await res.json();
        throw new Error(error.detail || "Failed to reactivate");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };
  
  const handleUpdatePayment = async () => {
    setActionLoading('payment');
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ return_url: window.location.href })
      });
      
      if (res.ok) {
        const { portal_url } = await res.json();
        window.location.href = portal_url;
      } else {
        const error = await res.json();
        throw new Error(error.detail || "Failed to open billing portal");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (!data) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Alert variant="destructive">
          <AlertDescription>Unable to load billing information</AlertDescription>
        </Alert>
      </div>
    );
  }
  
  const getStatusBadge = () => {
    if (data.subscription.cancel_at_period_end) {
      return <Badge className="bg-orange-100 text-orange-700">Canceling at Period End</Badge>;
    }
    switch (data.subscription.status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700">Active</Badge>;
      case 'past_due':
        return <Badge className="bg-red-100 text-red-700">Past Due</Badge>;
      case 'canceled':
        return <Badge variant="secondary">Canceled</Badge>;
      default:
        return <Badge variant="outline">No Subscription</Badge>;
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <CreditCard className="h-6 w-6" />
          <h1 className="text-2xl font-bold">Billing & Subscription</h1>
        </div>
        <p className="text-muted-foreground">
          Manage your subscription and payment methods
        </p>
      </div>
      
      {/* Current Subscription Card */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl">Current Plan</CardTitle>
              <CardDescription>Your active subscription details</CardDescription>
            </div>
            {getStatusBadge()}
          </div>
        </CardHeader>
        <CardContent>
          {data.is_free_tier ? (
            <div className="text-center py-6">
              <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <h3 className="text-lg font-semibold mb-1">Community Host (Free)</h3>
              <p className="text-muted-foreground mb-4">
                You&apos;re on the free tier. Other plans add a more complete business presence.
              </p>
              <Button onClick={() => navigate('/subscription')}>
                <ArrowRight className="h-4 w-4 mr-2" />
                View Plans
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Plan Details */}
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Plan</p>
                  <p className="text-lg font-semibold">{data.subscription.plan_name}</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Price</p>
                  <p className="text-lg font-semibold">
                    ${data.subscription.price}
                    {data.subscription.interval && (
                      <span className="text-sm font-normal text-muted-foreground">/{data.subscription.interval}</span>
                    )}
                  </p>
                </div>
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1 flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Next Billing
                  </p>
                  <p className="text-lg font-semibold">
                    {data.subscription.cancel_at_period_end 
                      ? 'Ends ' + formatDate(data.subscription.current_period_end)
                      : formatDate(data.subscription.current_period_end)
                    }
                  </p>
                </div>
              </div>
              
              {/* Warning for canceling subscriptions */}
              {data.subscription.cancel_at_period_end && (
                <Alert className="border-orange-200 bg-orange-50">
                  <AlertTriangle className="h-4 w-4 text-orange-600" />
                  <AlertDescription className="text-orange-800">
                    Your subscription is set to cancel on {formatDate(data.subscription.current_period_end)}. 
                    You will retain access until then.
                  </AlertDescription>
                </Alert>
              )}
              
              {/* Past due warning */}
              {data.subscription.status === 'past_due' && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Your payment is past due. Please update your payment method to avoid service interruption.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </CardContent>
        
        {!data.is_free_tier && (
          <CardFooter className="flex flex-wrap gap-3 border-t pt-4">
            {/* Update Payment Method */}
            <Button 
              variant="outline" 
              onClick={handleUpdatePayment}
              disabled={actionLoading === 'payment'}
            >
              {actionLoading === 'payment' ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CreditCard className="h-4 w-4 mr-2" />
              )}
              Update Payment Method
            </Button>
            
            {/* Cancel or Reactivate */}
            {data.can_reactivate ? (
              <Button 
                variant="default"
                onClick={handleReactivateSubscription}
                disabled={actionLoading === 'reactivate'}
              >
                {actionLoading === 'reactivate' ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Reactivate Subscription
              </Button>
            ) : data.can_cancel ? (
              <Button 
                variant="destructive"
                onClick={handleCancelSubscription}
                disabled={actionLoading === 'cancel'}
              >
                {actionLoading === 'cancel' ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <XCircle className="h-4 w-4 mr-2" />
                )}
                Cancel Subscription
              </Button>
            ) : null}
            
            {/* Change Plan */}
            <Button variant="ghost" onClick={() => navigate('/subscription')}>
              Change Plan
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </CardFooter>
        )}
      </Card>
      
      {/* Invoice History Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Invoice History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.invoices_available ? (
            <div className="space-y-2">
              {/* Invoice list would go here */}
              <p className="text-muted-foreground">Your invoices will appear here.</p>
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              <FileText className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p className="font-medium">{data.invoices_message}</p>
              <p className="text-sm mt-1">Check back later for your billing history.</p>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Quick Links */}
      <div className="mt-6 flex flex-wrap gap-4 text-sm">
        <Link to="/business/visibility" className="text-primary hover:underline flex items-center gap-1">
          Manage Visibility
          <ExternalLink className="h-3 w-3" />
        </Link>
        <Link to="/subscription" className="text-primary hover:underline flex items-center gap-1">
          View All Plans
          <ExternalLink className="h-3 w-3" />
        </Link>
        <Link to="/about-terms" className="text-muted-foreground hover:underline flex items-center gap-1">
          Terms & Conditions
          <ExternalLink className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
};

export default BusinessBilling;
